/* eslint-disable @typescript-eslint/no-explicit-any */
import { invariant } from '../../../common/src/utils/invariant'
import { BaseMetadata, EntityMetadata, TenantConfiguration, UserData } from '../base-metadata'
import { Conversion } from '../crud-interfaces'
import { DesignModel, EntityModel, MessageModel, OptionSetModel } from '../definitions'
import { compressToUTF16, decompressFromUTF16 } from './compression'
import {
  ModelsMetadataCompressionWorkerFactory,
  setModelsMetadataCompressionWorkerFactory,
  writeModelsMetadataAsync,
} from './models-metadata-compression'

type BASEMETADATA_STORAGE_KEY = 'BASE_METADATA'
type METADATA_STORAGE_KEY = 'MODELS_METADATA'
type USER_DATA = 'USER_DATA'
type SPECIAL_CLAIMS = 'SPECIAL_CLAIMS'
type TENANT_CONFIGURATION = 'TENANT_CONFIGURATION'
type MU_COONVERSIONS = 'MU_CONVERSIONS'

type StorageKeys =
  | BASEMETADATA_STORAGE_KEY
  | METADATA_STORAGE_KEY
  | USER_DATA
  | SPECIAL_CLAIMS
  | TENANT_CONFIGURATION
  | MU_COONVERSIONS

const store = window.sessionStorage
const memoryCache: Record<string, any> = {}

export function configureModelsMetadataCompressionWorkerFactory(
  factory: ModelsMetadataCompressionWorkerFactory | null,
): void {
  setModelsMetadataCompressionWorkerFactory(factory)
}

function updateSync<T>(key: StorageKeys, data: T) {
  const serializedData = JSON.stringify(data)
  const compressedData = compressToUTF16(serializedData)
  store.setItem(key, compressedData)
  memoryCache[key] = data
}

function updateModelsMetadata(data: DesignModel): Promise<void> {
  return writeModelsMetadataAsync(data, store, memoryCache)
}

function read<T>(key: StorageKeys): T | null {
  try {
    let entry = memoryCache[key]
    if (!entry) {
      const item = store.getItem(key)
      let res = null
      if (item) {
        const decoded = decompressFromUTF16(item)
        res = JSON.parse(decoded as string) as T
      }
      entry = res
      memoryCache[key] = res
    }
    return entry
  } catch (error) {
    invariant(error, 'Failed to read from session storage')
    throw new Error('💥', error)
  }
}

function update<T>(key: StorageKeys, data: T) {
  try {
    updateSync(key, data)
  } catch (error) {
    invariant(error, 'Failed to update from session storage')
    throw new Error('💥', error)
  }
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function deleteItem(key: StorageKeys) {
  try {
    store.removeItem(key)
    delete memoryCache[key]
  } catch (error) {
    throw new Error('💥')
  }
}

export function clearModelsMetadata() {
  deleteItem('MODELS_METADATA')
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function clearStorage() {
  try {
    store.clear()
    Object.keys(memoryCache).forEach((k) => {
      delete memoryCache[k]
    })
  } catch (error) {
    throw new Error('💥')
  }
}

/**
 * The function reads a DesignModel from a metadata source and returns a new DesignModel object that
 * merges the retrieved data with an existing DesignModel object.
 * @returns an instance of the `DesignModel` class.
 */
export function readFullModel(): DesignModel {
  const r = read<DesignModel>('MODELS_METADATA')
  if (r instanceof DesignModel) {
    r
  }
  const retVal = new DesignModel()
  retVal.merge(r as any, undefined)
  return retVal
}

/**
 * This function reads an entity model by name from a design model.
 * @param {string} name - The `name` parameter is a string that represents the name of an entity in a
 * design model. This function reads the metadata of the design model from the local storage and
 * returns the entity model with the specified name. If the entity model is not found, it returns
 * `undefined`.
 * @returns an instance of the `EntityModel` class or `undefined`. The `getEntity` method is called on
 * the `retVal` object, which is an instance of the `DesignModel` class, to retrieve the `EntityModel`
 * with the specified `name`. If the `EntityModel` is found, it is returned, otherwise `undefined` is
 * returned.
 */
export function readEntityModelByName(name: string): EntityModel | undefined {
  const r = read<DesignModel>('MODELS_METADATA')
  if (r instanceof DesignModel) {
    return r.getEntity(name)
  }
  const retVal = new DesignModel()
  retVal.merge(r as any, undefined)
  return retVal.getEntity(name)
}

export function readEntityModelIncludeExternal(name: string, msName: string): EntityModel | undefined {
  let r = read<DesignModel>('MODELS_METADATA')
  if (!(r instanceof DesignModel)) {
    const retVal = new DesignModel()
    retVal.merge(r as any, undefined)
    r = retVal
  }

  let model = r.getExternalEntity(name, msName)
  if (!model) {
    model = r.getEntity(name)
  }
  return model
}

export function readOptionSetModelByName(name: string): OptionSetModel | undefined {
  const r = read<DesignModel>('MODELS_METADATA')
  if (r instanceof DesignModel) {
    return r.getOptionSet(name)
  }
  const retVal = new DesignModel()
  retVal.merge(r as any, undefined)
  return retVal.getOptionSet(name)
}

export function readMessageModelByCode(code: string): MessageModel | undefined {
  const r = read<DesignModel>('MODELS_METADATA')
  if (r instanceof DesignModel) {
    return r.getMessages(code)
  }
  const retVal = new DesignModel()
  retVal.merge(r as any, undefined)
  return retVal.getMessages(code)
}

/**
 * This TypeScript function reads base metadata by name and returns the corresponding entity metadata.
 * @param {string} name - A string representing the name of the entity metadata that needs to be
 * retrieved.
 * @returns The function `readBaseMetadataByName` returns an `EntityMetadata` object that matches the
 * given `name` parameter. If no matching entity is found, it returns `undefined`.
 */
export function readBaseMetadataByName(name: string): EntityMetadata | undefined {
  const items = read<BaseMetadata>('BASE_METADATA')
  return items?.entities.find((e) => e.name === name)
}

/**
 * Search base metadata for the given entity this method also takes the form/view
 * this allows to also search for external entities
 * @param name the name of the entity
 * @param moduleName the name of the view of the form
 */
export function readBaseMetadataByNameAndModule(name: string, moduleName: string): EntityMetadata | undefined {
  const items = read<BaseMetadata>('BASE_METADATA')
  let res = items?.entities.find(
    (e) =>
      e.name === name &&
      (e.formEntries.find((f) => f.name === moduleName) || e.viewEntries.find((v) => v.name === moduleName)),
  )

  if (!res) {
    res = items?.externalEntities.find(
      (e) =>
        e.name === name &&
        (e.formEntries.find((f) => f.name === moduleName) || e.viewEntries.find((v) => v.name === moduleName)),
    )
  }

  return res
}

/**
 * Search base metadata for the given entity this method also takes the form/view
 * this allows to also search for external entities
 * @param name the name of the entity
 * @param moduleName the name of the view of the form
 */
export function readBaseMetadataForLinkedEntities(entityName: string, moduleName: string) {
  /**
   * if linked there is no moduleName
   */
  return moduleName ? readBaseMetadataByNameAndModule(entityName, moduleName) : readBaseMetadataByName(entityName)
}

/**
 * This function synchronizes a given DesignModel with the MODELS_METADATA stored in the system.
 * @param {DesignModel} model - The `model` parameter is an instance of the `DesignModel` class, which
 * is being passed as an argument to the `syncEntityModel` function. This model represents the data
 * that needs to be synchronized with the existing `MODELS_METADATA` data.
 */
export async function syncEntityModel(model: DesignModel, msName?: string): Promise<void> {
  const toSave = new DesignModel()
  const models = read<DesignModel>('MODELS_METADATA')
  if (models !== null) {
    toSave.merge(models, undefined)
  }
  toSave.merge(model, msName)
  try {
    await updateModelsMetadata(toSave)
  } catch (error) {
    invariant(error, 'Failed to update models metadata in session storage')
    throw new Error('💥', error)
  }
}

/**
 * The function syncs the base metadata of models.
 * @param {BaseMetadata} models - The `models` parameter is an object of type `BaseMetadata` that
 * contains metadata information about different models in an application. This metadata can include
 * information such as the name, description, fields, and relationships of each model. The
 * `syncBaseMetadata` function takes this object as a parameter and updates
 */
export function syncBaseMetadata(models: BaseMetadata) {
  update('BASE_METADATA', models)
}

/**
 * The function syncUserData updates the user data models.
 * @param {UserData} models - The parameter `models` is of type `UserData`, which is likely an
 * interface or a type defined elsewhere in the codebase. It represents an object that contains data
 * related to a user, such as their name, email, address, etc. The `syncUserData` function takes this
 * object as an
 */
export function syncUserData(models: UserData) {
  update('USER_DATA', models)
}

/**
 * The function `syncSpecialClaims` updates the `SPECIAL_CLAIMS` with the provided claims.
 * @param {string[]} claims - An array of strings representing special claims.
 */
export function syncSpecialClaims(claims: string[]) {
  update('SPECIAL_CLAIMS', claims)
}

/**
 * The function `syncTenantConfiguration` updates the `TENANT_CONFIGURATION` with the current tenat configuration.
 * @param {TenantConfiguration} tenantConfiguration
 */
export function syncTenantConfiguration(tenantConfiguration: TenantConfiguration) {
  update('TENANT_CONFIGURATION', tenantConfiguration)
}

export function syncMuConversions(conversions: Record<string, Conversion>) {
  update('MU_CONVERSIONS', conversions)
}

/**
 * This function reads and returns user data from local storage.
 * @returns The function `readUserData` is returning the value of the `read` function called with the
 * argument `'USER_DATA'` casted as a `UserData` type.
 */
export function readUserData(): UserData {
  return read('USER_DATA') as UserData
}

/**
 * The function `readSpecialClaims` reads and returns an array of special claims from a source called
 * 'SPECIAL_CLAIMS'.
 * @returns The function `readSpecialClaims` is returning an array of strings.
 */
export function readSpecialClaims(): string[] {
  return read('SPECIAL_CLAIMS') as string[]
}

/**
 * The function `readTeantConfiguration` reads and returns configuration for tenant from a source called
 * 'TENANT_CONFIGURATION'.
 * @returns The function `readTeantConfiguration` return TenantConfiguration
 */
export function readTeantConfiguration(): TenantConfiguration {
  return read('TENANT_CONFIGURATION') as TenantConfiguration
}

export function readMuConversions(): Record<string, Conversion> {
  return read('MU_CONVERSIONS') as Record<string, Conversion>
}
